import type { ChannelType, DeliveryStatus } from './channel-types';

/**
 * Result returned by a channel's `deliver()` method.
 */
export interface DeliveryResult {
  /** Outcome of the delivery attempt. */
  status: 'delivered' | 'failed' | 'retry';
  /** Optional response data to store alongside the delivery record. */
  responseData?: Record<string, unknown>;
  /** Human-readable error description when status is `failed` or `retry`. */
  errorMessage?: string;
  /** For `retry` status: delay in milliseconds before the next attempt. */
  retryAfterMs?: number;
}

/**
 * Minimal notification shape passed to channel implementations.
 * Avoids coupling channels to the full Drizzle row type.
 */
export interface NotificationPayload {
  id: string;
  eventId: string;
  recipientId: string;
  title: string;
  message: string;
  type: string;
  priority: string;
  category: string | null;
  metadata: Record<string, unknown> | null;
}

/**
 * Minimal delivery shape passed to channel implementations.
 */
export interface DeliveryPayload {
  id: string;
  notificationId: string;
  channel: ChannelType;
  status: DeliveryStatus;
  attemptCount: number;
  maxAttempts: number;
}

/**
 * Minimal event shape passed to channel implementations.
 */
export interface EventPayload {
  id: string;
  eventType: string;
  sourceType: string;
  sourceId: string | null;
  payload: Record<string, unknown>;
}

/**
 * Pluggable channel interface.
 *
 * Each delivery channel (in-app, webhook, email, etc.) implements this
 * interface and is registered via NestJS multi-provider injection.
 */
export interface NotificationChannel {
  /** The channel type this implementation handles. */
  readonly channelType: ChannelType;

  /**
   * Pre-flight check: can this channel deliver to the given recipient?
   * Return `false` to skip delivery without creating a failure record.
   *
   * Examples:
   * - `InAppChannel` always returns `true`
   * - `WebhookChannel` returns `true` only when active endpoints match
   * - `EmailChannel` returns `true` only if the person has a verified email
   */
  canDeliver(notification: NotificationPayload, recipientId: string): Promise<boolean>;

  /**
   * Attempt to deliver the notification through this channel.
   *
   * The processor calls this method and updates the delivery record
   * based on the returned `DeliveryResult`.
   */
  deliver(notification: NotificationPayload, delivery: DeliveryPayload, event: EventPayload, db?: unknown): Promise<DeliveryResult>;
}

/**
 * Injection token for the multi-provider array of `NotificationChannel`.
 */
export const NOTIFICATION_CHANNELS = Symbol('NOTIFICATION_CHANNELS');
